import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, AlertOctagon, Copy, GitBranch, GitMerge, GitPullRequest, GitBranchPlus, Database, DatabaseBackup, List, Info, Activity, Search, ShieldCheck, Key, KeyRound, Rocket, RotateCcw, ArrowUpCircle, XCircle as XCircleIcon, CheckCircle, Save, Clock, Loader, Gauge, Moon, Trash2 } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { ReactComponent as PlanetScaleIcon } from '../assets/icons/planetscale-mono.svg';

const POLL_INTERVAL_OPTIONS = [
  { value: 60, label: '60 seconds' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
];

const formatPollInterval = (seconds?: number): string => {
  if (!seconds) return '15 minutes';
  if (seconds % 60 === 0) {
    const mins = seconds / 60;
    return mins === 1 ? '1 minute' : `${mins} minutes`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
};

// PlanetScale generates a unique signing secret per webhook (one per database),
// so the user pastes the secret(s) PlanetScale shows them rather than Kestrel
// generating one. Multiple databases => multiple secrets; the receiver verifies
// each delivery against any of them.

const PlanetScaleIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleRepo, setExampleRepo] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);
  const [resourcesTracked, setResourcesTracked] = useState(0);
  const [pollInterval, setPollInterval] = useState(300);
  const [organization, setOrganization] = useState('');
  const [webhookConfigured, setWebhookConfigured] = useState(false);
  const [webhookSecretCount, setWebhookSecretCount] = useState(0);
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);
  const [copied, setCopied] = useState(false);

  const [tokenId, setTokenId] = useState('');
  const [token, setToken] = useState('');
  const [connectOrg, setConnectOrg] = useState('');
  // PlanetScale generates a signing secret per webhook (one per database). The
  // user pastes each secret here; an empty trailing field lets them add more.
  const [webhookSecrets, setWebhookSecrets] = useState<string[]>(['']);
  const [connectPollInterval, setConnectPollInterval] = useState(300);

  const webhookUrl = `${window.location.origin}/api/webhooks/planetscale`;

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getPlanetScaleStatus();
      if (data?.connected) {
        setConnected(true);
        setLastPollAt(data.last_poll_at || null);
        setResourcesTracked(data.resources_tracked || 0);
        setPollInterval(data.poll_interval || 900);
        setOrganization(data.organization || '');
        setWebhookConfigured(!!data.webhook_configured);
        setWebhookSecretCount(data.webhook_secret_count || 0);
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      const secrets = webhookSecrets.map(s => s.trim()).filter(Boolean);
      await apiClient.connectPlanetScale({
        token_id: tokenId,
        token: token,
        organization: connectOrg,
        webhook_secrets: secrets.length > 0 ? secrets : undefined,
        poll_interval: connectPollInterval,
      });
      setSuccessMsg('PlanetScale connected successfully!');
      setToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectPlanetScale();
      setConnected(false);
      setSuccessMsg('PlanetScale disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testPlanetScale();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to PlanetScale (${data.database_count ?? 0} database${data.database_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const updateSecret = (index: number, value: string) => {
    setWebhookSecrets(prev => {
      const next = [...prev];
      next[index] = value;
      // Keep one empty trailing field so the user can always add another.
      if (index === next.length - 1 && value.trim() !== '') {
        next.push('');
      }
      return next;
    });
  };

  const removeSecret = (index: number) => {
    setWebhookSecrets(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [''];
    });
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {/* BODY_PLACEHOLDER */}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <PlanetScaleIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  PlanetScale
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Database branches, deploy requests (schema migrations), backups, branch passwords, and investigation automation</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {organization && (
                <div>
                  <span className="text-muted-foreground">Organization</span>
                  <p className="font-medium">{organization}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Backups Tracked</span>
                <p className="font-medium">{resourcesTracked.toLocaleString()} backup{resourcesTracked !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Backup Poll Interval</span>
                <p className="font-medium">{formatPollInterval(pollInterval)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Webhook</span>
                <p className="font-medium">{webhookConfigured ? `${webhookSecretCount} secret${webhookSecretCount !== 1 ? 's' : ''} configured (${webhookEventCount} event${webhookEventCount !== 1 ? 's' : ''})` : 'Not configured'}</p>
              </div>
              {lastWebhookAt && (
                <div>
                  <span className="text-muted-foreground">Last Webhook</span>
                  <p className="font-medium">{new Date(lastWebhookAt).toLocaleString()}</p>
                </div>
              )}
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last Backup Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Webhook</CardTitle>
            <CardDescription>Register a PlanetScale webhook per database to trigger workflows on branch and deploy-request events. Backup triggers work via polling without this.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-sm">Webhook URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={copyWebhookUrl} className="gap-1 shrink-0">
                  {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                In the PlanetScale dashboard (<code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">app.planetscale.com</code>), open each database's <strong className="text-foreground">Settings → Webhooks → Add webhook</strong> (or run <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">pscale webhook create &lt;database&gt;</code>), set this URL, and select the events. Branch and storage events are available for all databases; the deploy-request events are available on Vitess (MySQL) databases only. PlanetScale generates a unique signing secret per webhook — to register a new database's secret, paste it on the <strong className="text-foreground">Reconnect</strong> form below; existing secrets are kept.
              </p>
            </div>
          </CardContent>
        </Card>
        {/* BLOCKS_PLACEHOLDER */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these PlanetScale actions and triggers in your workflows to automate schema migrations, CI/CD, incident response, and database hygiene.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Deploy Request Opened', desc: 'Triggers when a deploy request (schema migration) is opened.', Icon: GitPullRequest },
                  { name: 'Deploy Request Queued', desc: 'Triggers when a deploy request is added to the deploy queue.', Icon: Clock },
                  { name: 'Deploy Request In Progress', desc: 'Triggers when a deploy request starts running.', Icon: Loader },
                  { name: 'Deploy Request Schema Applied', desc: 'Triggers when a deploy request finishes applying the schema.', Icon: CheckCircle },
                  { name: 'Deploy Request Errored', desc: 'Triggers when a deploy request stops due to an error.', Icon: AlertOctagon },
                  { name: 'Deploy Request Reverted', desc: 'Triggers when a deploy request is reverted.', Icon: RotateCcw },
                  { name: 'Deploy Request Closed', desc: 'Triggers when a deploy request is closed.', Icon: XCircleIcon },
                  { name: 'Branch Ready', desc: 'Triggers when a branch is created and ready to connect.', Icon: GitBranch },
                  { name: 'Branch Anomaly (Insights)', desc: 'Triggers on a new insights anomaly (e.g. slow query, replication lag).', Icon: AlertTriangle },
                  { name: 'Branch Primary Promoted', desc: 'Triggers when a new primary is promoted for a branch.', Icon: ArrowUpCircle },
                  { name: 'Branch Sleeping', desc: 'Triggers when a branch goes to sleep (cost signal).', Icon: Moon },
                  { name: 'Storage Threshold', desc: 'Triggers when a cluster/keyspace reaches a storage threshold.', Icon: Gauge },
                  { name: 'Backup Completed', desc: 'Triggers when a branch backup completes (detected via polling).', Icon: DatabaseBackup },
                  { name: 'Backup Failed', desc: 'Triggers when a branch backup fails (detected via polling).', Icon: AlertTriangle },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'List Databases', desc: 'List the databases in the organization.', Icon: List },
                  { name: 'Get Database', desc: 'Fetch a database\u2019s state and region.', Icon: Info },
                  { name: 'List Branches', desc: 'List a database\u2019s branches.', Icon: List },
                  { name: 'Get Branch', desc: 'Fetch a branch\u2019s ready/production state.', Icon: Info },
                  { name: 'Create Branch', desc: 'Create a branch from a parent branch.', Icon: GitBranchPlus },
                  { name: 'Delete Branch', desc: 'Delete a branch (gate behind Approval).', Icon: Trash2 },
                  { name: 'Promote Branch to Production', desc: 'Promote a branch to production (gate behind Approval).', Icon: ArrowUpCircle },
                  { name: 'Set Safe Migrations', desc: 'Enable/disable safe migrations on a branch.', Icon: ShieldCheck },
                  { name: 'List Deploy Requests', desc: 'List deploy requests for a database.', Icon: List },
                  { name: 'Get Deploy Request', desc: 'Fetch a deploy request\u2019s state.', Icon: Info },
                  { name: 'Create Deploy Request', desc: 'Open a schema migration deploy request.', Icon: GitPullRequest },
                  { name: 'Deploy Deploy Request', desc: 'Apply a schema change (gate behind Approval).', Icon: Rocket },
                  { name: 'Revert Deploy Request', desc: 'Revert a deployed change (gate behind Approval).', Icon: RotateCcw },
                  { name: 'Close Deploy Request', desc: 'Close an open deploy request without deploying.', Icon: XCircleIcon },
                  { name: 'Approve Deploy Request', desc: 'Post an approval review on a deploy request.', Icon: CheckCircle },
                  { name: 'List Backups', desc: 'List a branch\u2019s backups.', Icon: DatabaseBackup },
                  { name: 'Create Backup', desc: 'Create a backup of a branch.', Icon: Save },
                  { name: 'List Branch Credentials', desc: 'List a branch\u2019s credentials (passwords on MySQL/Vitess, roles on Postgres).', Icon: Key },
                  { name: 'Create Branch Credential', desc: 'Create a branch credential (password on MySQL/Vitess, role on Postgres).', Icon: KeyRound },
                  { name: 'Delete Branch Credential', desc: 'Delete a branch credential by ID (gate behind Approval).', Icon: Trash2 },
                  { name: 'Investigate PlanetScale', desc: 'AI investigates databases, branches, deploy requests, and backups.', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a pull request is merged in{' '}
              <input
                value={exampleRepo}
                onChange={e => setExampleRepo(e.target.value)}
                placeholder="org/repo"
                style={{ width: `${Math.max(exampleRepo.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />, create a PlanetScale deploy request, require approval, deploy the schema change, and post the result to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#releases"
                style={{ width: `${Math.max(exampleChannel.length || 9, 9)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleRepo || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a pull request is merged in ${exampleRepo}, create a PlanetScale deploy request, require approval, deploy the schema change, and post the result to ${exampleChannel} on Slack.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>{/* CONNECT_PLACEHOLDER */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <PlanetScaleIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle>PlanetScale</CardTitle>
                <CardDescription>Connect PlanetScale for database branch, deploy-request (schema migration), backup, and credential automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Service Token</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to PlanetScale with a <strong className="text-foreground">service token</strong> (ID + token), which it sends to the PlanetScale API at <code className="bg-muted px-1 rounded">api.planetscale.com</code> to read and manage branches, deploy requests, and backups. Create one in the dashboard at <strong className="text-foreground">app.planetscale.com → Settings → Service tokens → New service token</strong> (or with <code className="bg-muted px-1 rounded">pscale service-token create</code>), then grant the permissions below.
              </p>
              <div className="rounded-md border border-input bg-muted/40 p-3 mb-3 text-xs text-muted-foreground space-y-2">
                <p>After creating the token, click <strong className="text-foreground">Add organization permissions</strong> and <strong className="text-foreground">Select permissions</strong> (under Database access) and enable:</p>
                <div>
                  <p className="font-medium text-foreground">Organization access</p>
                  <ul className="list-disc pl-4 mt-1 space-y-1">
                    <li><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">read_databases</code> — list and inspect the organization's databases (required; used to validate the connection and populate dropdowns)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-foreground">Database access</p>
                  <p className="text-[11px] mt-0.5">Use <strong className="text-foreground">Permissions for all databases</strong> to cover every database, or scope to specific ones. Enable the permissions for the blocks you'll use:</p>
                  <ul className="list-disc pl-4 mt-1 space-y-1">
                    <li><span className="inline-flex flex-wrap gap-1 align-middle"><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">read_branch</code><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">create_branch</code><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">delete_branch</code></span> — branch blocks (create/get/list/delete, promote, safe migrations)</li>
                    <li><span className="inline-flex flex-wrap gap-1 align-middle"><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">read_deploy_request</code><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">create_deploy_request</code><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">approve_deploy_request</code></span> — deploy-request blocks (create/get/list/deploy/revert/close/approve)</li>
                    <li><span className="inline-flex flex-wrap gap-1 align-middle"><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">read_backups</code><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">write_backups</code></span> — backup blocks (list/create)</li>
                    <li><code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">connect_branch</code> (and <code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">connect_production_branch</code> for production) — branch password blocks (list/create/delete)</li>
                  </ul>
                </div>
                <p className="text-[11px]">For a read-only setup (investigate + list/get blocks only), the <code className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">read_*</code> permissions alone are enough.</p>
              </div>
              <Label htmlFor="ps-token-id" className="text-xs text-muted-foreground">Service Token ID</Label>
              <Input
                id="ps-token-id"
                value={tokenId}
                onChange={e => setTokenId(e.target.value)}
                placeholder="Service token ID"
                className="mt-1 mb-2 font-mono"
              />
              <Label htmlFor="ps-token" className="text-xs text-muted-foreground">Service Token</Label>
              <Input
                id="ps-token"
                type="password"
                value={token}
                onChange={e => setToken(e.target.value)}
                placeholder="pscale_tkn_..."
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the PlanetScale API.
              </p>
            </div>
            <div>
              <Label htmlFor="ps-org" className="text-sm">Organization</Label>
              <Input
                id="ps-org"
                value={connectOrg}
                onChange={e => setConnectOrg(e.target.value)}
                placeholder="your-organization"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">The PlanetScale organization name (slug) whose databases Kestrel will manage.</p>
            </div>
            <div>
              <Label htmlFor="ps-poll" className="text-sm">Backup Poll Interval</Label>
              <select
                id="ps-poll"
                value={connectPollInterval}
                onChange={e => setConnectPollInterval(Number(e.target.value))}
                className="mt-1 w-full sm:w-auto block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {POLL_INTERVAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Branch and deploy-request events arrive via webhook in real time. PlanetScale has no backup webhook, so Kestrel polls the backups API at this cadence to detect backup completed/failed events. The minimum is 60 seconds.
              </p>
            </div>
            <div>
              <Label className="text-sm">Webhook</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Needed for the branch and deploy-request triggers. Register a PlanetScale webhook on each database you want to watch, then paste the secret PlanetScale generates for each one below. Backup triggers are detected by polling and need no webhook.
              </p>
              <div className="rounded-md border border-input bg-muted/40 p-4 mb-3">
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                    <span>In the <strong className="text-foreground">PlanetScale dashboard</strong> (<code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">app.planetscale.com</code>), open the database, go to <strong className="text-foreground">Settings → Webhooks</strong>, and click <strong className="text-foreground">Add webhook</strong> (or run <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">pscale webhook create &lt;database&gt;</code>).</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                    <div className="min-w-0 flex-1">
                      <span>Set the <strong className="text-foreground">URL</strong> to:</span>
                      <div className="relative mt-1.5">
                        <pre className="text-xs font-mono bg-background border border-input rounded px-3 py-2 pr-10 overflow-x-auto whitespace-pre">{webhookUrl}</pre>
                        <button
                          type="button"
                          onClick={copyWebhookUrl}
                          aria-label="Copy webhook URL"
                          className="absolute top-1.5 right-1.5 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        >
                          {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                    <div className="min-w-0 flex-1">
                      <span>Under <strong className="text-foreground">Events</strong>, select the ones you want. The available events depend on the database engine:</span>
                      <ul className="list-disc pl-4 mt-1 space-y-0.5">
                        <li><strong className="text-foreground">All databases:</strong> <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">branch.ready</code>, <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">branch.anomaly</code>, <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">branch.primary_promoted</code>, <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">branch.sleeping</code>, and <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">cluster.storage</code>.</li>
                        <li><strong className="text-foreground">Vitess (MySQL) databases:</strong> also the deploy-request lifecycle (<code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">deploy_request.opened</code>, <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">deploy_request.errored</code>, <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">deploy_request.closed</code>, …). Postgres databases don't expose deploy-request events.</li>
                      </ul>
                      <span className="block mt-1">Keep <strong className="text-foreground">Enable this webhook</strong> checked, then click <strong className="text-foreground">Save webhook</strong>.</span>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                    <div className="min-w-0 flex-1">
                      <span>PlanetScale shows a <strong className="text-foreground">Webhook secret</strong> for the new webhook (also under <strong className="text-foreground">⋯ → Show secret</strong>). Copy it and paste it below. Add one secret per database webhook.</span>
                      <div className="space-y-2 mt-2">
                        {webhookSecrets.map((secret, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Input
                              type="password"
                              value={secret}
                              onChange={e => updateSecret(i, e.target.value)}
                              placeholder={i === 0 ? 'Webhook secret from PlanetScale' : 'Another database\u2019s webhook secret (optional)'}
                              className="font-mono text-xs"
                            />
                            {webhookSecrets.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeSecret(i)}
                                aria-label="Remove secret"
                                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors shrink-0"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">PlanetScale generates a unique secret per webhook, so paste each database's secret. Kestrel stores them encrypted and verifies every delivery against any of them. You can add more later by reconnecting.</p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
            <Button onClick={handleConnect} disabled={saving || !tokenId || !token || !connectOrg}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect PlanetScale
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default PlanetScaleIntegration;
